import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import {
  ChevronRight, ShieldCheck, Zap, BadgeCheck, Lock, Sparkles,
  Battery, Cable, Camera, Volume2, Droplet, HardDrive, Wrench, Mic,
  ArrowRight, MessageCircle, Phone, Star, MapPin, Award, ReceiptText, Clock,
  HeartHandshake, ExternalLink, X, ZoomIn, Mail, Navigation, Images,
  Instagram, Facebook, Youtube, Music2, Ghost, Store,
} from "lucide-react";
import { api } from "@/lib/api";
import { useSiteContent } from "@/lib/useSiteContent";
import { useCompany, toWhatsAppHref, toTelHref, formatHours, DAY_KEYS, DAY_LABELS_SHORT } from "@/lib/useCompany";
import { useDataRefresh } from "@/lib/useDataRefresh";
import AnimatedStat from "@/components/home/AnimatedStat";

// Icon map used for CMS-driven items (icon name → lucide component).
const ICON_MAP = {
  Sparkles, Battery, Cable, Camera, Volume2, Droplet, HardDrive, Wrench, Mic,
  ShieldCheck, Zap, BadgeCheck, Lock, Award, ReceiptText, HeartHandshake,
};

const SHOW_EXTENDED_HOME_SECTIONS = false;

const LOCATION_SOCIALS = {
  instagram: { label: "Instagram", icon: Instagram },
  facebook: { label: "Facebook", icon: Facebook },
  tiktok: { label: "TikTok", icon: Music2 },
  snapchat: { label: "Snapchat", icon: Ghost },
  youtube: { label: "YouTube", icon: Youtube },
};

function getLocationStatus(location, now = new Date()) {
  const keys = ["su", "mo", "tu", "we", "th", "fr", "sa"];
  const hours = location?.opening_hours?.[keys[now.getDay()]];
  if (!hours || hours.closed || !hours.open_time || !hours.close_time) return { open: false, label: "Geschlossen" };
  const minutes = now.getHours() * 60 + now.getMinutes();
  const [openHour, openMinute] = hours.open_time.split(":").map(Number);
  const [closeHour, closeMinute] = hours.close_time.split(":").map(Number);
  const open = minutes >= openHour * 60 + openMinute && minutes < closeHour * 60 + closeMinute;
  return { open, label: open ? `Geöffnet bis ${hours.close_time} Uhr` : "Geschlossen" };
}


export default function Home() {
  const brands = [];
  const reviews = [];
  const faqs = [];
  const [activeLocation, setActiveLocation] = useState(undefined);
  const [locationGalleryOpen, setLocationGalleryOpen] = useState(false);
  const [locationGalleryIndex, setLocationGalleryIndex] = useState(0);
  const [galleryImage, setGalleryImage] = useState(null);
  const site = useSiteContent();
  const company = useCompany();

  const load = useCallback(() => {
    api.get("/locations").then(r => setActiveLocation(r.data?.[0] || null)).catch(() => setActiveLocation(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  useDataRefresh(load);

  const popularRepairs = Array.isArray(site.home_popular_repairs_items) ? site.home_popular_repairs_items : [];
  const steps = Array.isArray(site.home_how_it_works_steps) ? site.home_how_it_works_steps : [];
  const whyBullets = Array.isArray(site.home_why_us_bullets) ? site.home_why_us_bullets : [];
  const qualityCols = Array.isArray(site.home_quality_columns) ? site.home_quality_columns : [];
  const qualityRows = Array.isArray(site.home_quality_rows) ? site.home_quality_rows : [];
  const trustItems = Array.isArray(site.home_trust_items) ? site.home_trust_items : [];
  const statsItems = Array.isArray(site.home_stats_items) ? site.home_stats_items : [];
  const galleryItems = Array.isArray(site.home_gallery_items) ? site.home_gallery_items : [];


  const locationStatus = getLocationStatus(activeLocation);
  const locationGallery = Array.isArray(activeLocation?.gallery_images) ? activeLocation.gallery_images.filter((image) => image?.url) : [];
  const locationButtons = activeLocation?.button_texts || {};

  return (
    <div>
      {/* HERO */}
      <section className="relative isolate min-h-[760px] overflow-hidden border-b border-white/10 bg-[#08080a]" data-testid="home-hero">
        <div className="grain-overlay -z-10" />
        <div className="absolute inset-y-0 right-0 -z-10 w-full bg-[#0055FF]/[0.035] lg:w-[46%]" />
        <div className="absolute -right-40 top-20 -z-10 h-[34rem] w-[34rem] rounded-full bg-[#0055FF]/15 blur-3xl" />
        <div className="mx-auto max-w-7xl px-4 pb-20 pt-16 sm:px-6 sm:pt-20 lg:px-8 lg:pb-28 lg:pt-24">
          <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#4d9aff]/25 bg-[#0055FF]/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#75b2ff]" data-testid="hero-eyebrow">
                <MapPin size={14} /> Smartphone-Reparatur in {activeLocation?.city || "Kleve"}
              </div>
              <h1 className="mt-7 max-w-4xl font-display text-4xl font-black leading-[0.94] tracking-tighter text-white sm:text-5xl lg:text-6xl" data-testid="hero-heading">
                Smartphone kaputt?<br />
                <span className="text-[#4d9aff]">Wir bringen es zurück.</span>
              </h1>
              <p className="mt-7 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg" data-testid="hero-description">
                Professionelle Smartphone-Reparaturen für Apple, Samsung, Google, Xiaomi und viele weitere Marken. Original- und Premium-Ersatzteile, transparente Preise und schnelle Reparaturen – direkt in Kleve.
              </p>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link to="/reparatur" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#0055FF] px-6 py-3 text-base font-bold text-white shadow-lg shadow-[#0055FF]/20 transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#1768ff] focus:outline-none focus:ring-2 focus:ring-[#4d9aff]" data-testid="hero-start-repair">
                  Reparatur starten <ArrowRight size={18} />
                </Link>
                <a
                  href={toWhatsAppHref(activeLocation?.whatsapp || company.whatsapp)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-zinc-950 transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  data-testid="hero-whatsapp"
                >
                  <MessageCircle size={17} /> WhatsApp
                </a>
                <Link to="/preise" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/15 bg-[#141417] px-6 py-3 text-sm font-bold text-zinc-100 transition-[transform,border-color,background-color] duration-200 hover:-translate-y-0.5 hover:border-white/30 hover:bg-[#1c1c20] focus:outline-none focus:ring-2 focus:ring-[#4d9aff]" data-testid="hero-prices">
                  Preise ansehen <ChevronRight size={17} />
                </Link>
              </div>

              <ul className="mt-10 grid max-w-3xl grid-cols-1 gap-x-6 gap-y-3 border-t border-white/10 pt-7 sm:grid-cols-2 xl:grid-cols-3" data-testid="hero-trust-list">
                {[
                  [Zap, "Express-Reparatur"],
                  [ShieldCheck, "12 Monate Garantie"],
                  [BadgeCheck, "Original & Premium Teile"],
                  [ReceiptText, "Transparente Preise"],
                  [Lock, "Sichere Datenbehandlung"],
                ].map(([Icon, label], i) => (
                  <li key={label} className="flex items-center gap-2.5 text-sm text-zinc-300" data-testid={`hero-trust-${i}`}>
                    <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-[#0055FF]/12"><Icon size={14} className="text-[#4d9aff]" /></span>{label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5" data-testid="hero-visual">
              <div className="relative mx-auto max-w-[520px]">
                <div className="absolute -inset-5 -z-10 rounded-[2.75rem] border border-[#4d9aff]/15 bg-[#0055FF]/5" />
                <div className="group relative aspect-[4/5] overflow-hidden rounded-[2.25rem] border border-white/10 bg-[#141417] shadow-2xl shadow-black/50">
                  <img
                    src={site.hero_image_url || "https://images.pexels.com/photos/719399/pexels-photo-719399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
                    alt={site.hero_image_alt || "Professionelle Smartphone-Reparatur"}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.025]"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-transparent" />
                  <div className="absolute left-5 right-5 top-5 flex items-center justify-between gap-3">
                    <span className="rounded-full border border-white/15 bg-[#0a0a0c]/85 px-3 py-1.5 text-xs font-semibold text-zinc-200 backdrop-blur-xl">Apple · Samsung · Google</span>
                    <span className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-[#0a0a0c]/85 backdrop-blur-xl"><Wrench size={17} className="text-[#4d9aff]" /></span>
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/10 bg-[#101014]/95 p-5 backdrop-blur-xl" data-testid="hero-service-badge">
                    <div className="flex items-center gap-4">
                      <div className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-emerald-500/15"><BadgeCheck size={21} className="text-emerald-400" /></div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-500">Geprüfter Service</div>
                        <div className="mt-1 text-sm font-semibold text-white">Schnell diagnostiziert. Präzise repariert.</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-white/10 bg-[#141417] p-4 shadow-xl sm:block" data-testid="hero-price-promise">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">Unser Versprechen</div>
                  <div className="mt-1 flex items-center gap-2 text-sm font-semibold"><ReceiptText size={16} className="text-[#4d9aff]" /> Preis vor Reparatur</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST GRID + STATISTICS */}
      {site.section_trust_grid !== false && (
        <section className="premium-section section-rule bg-[#0e0e11]" data-testid="premium-trust-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="section-label text-[#4d9aff]">{site.home_trust_eyebrow}</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">{site.home_trust_title}</h2>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
              {trustItems.map((item, i) => {
                const Icon = ICON_MAP[item.icon] || ShieldCheck;
                return (
                  <div key={`${item.title}-${i}`} className={`premium-card p-6 ${i === 0 || i === 5 ? "lg:col-span-2" : ""}`} data-testid={`premium-trust-${i}`}>
                    <div className="h-11 w-11 rounded-xl bg-[#0055FF]/15 grid place-items-center"><Icon size={21} className="text-[#4d9aff]"/></div>
                    <h3 className="mt-5 font-display text-xl font-semibold">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-400 max-w-md">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {site.section_statistics !== false && (
        <section className="section-rule bg-[#0055FF] text-white" data-testid="home-statistics">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {statsItems.map((stat, i) => (
              <div key={`${stat.label}-${i}`} className="border-l border-white/30 pl-5" data-testid={`home-stat-${i}`}>
                <div className="font-display text-3xl sm:text-4xl font-black tabular-nums"><AnimatedStat value={stat.value}/></div>
                <div className="mt-1 text-sm text-blue-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* POPULAR REPAIRS */}
      {SHOW_EXTENDED_HOME_SECTIONS && site.section_popular_repairs !== false && (
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="popular-repairs">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="section-label">{site.home_popular_repairs_eyebrow}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              {site.home_popular_repairs_title}
            </h2>
          </div>
          <Link to="/preise" className="text-sm text-[#0055FF] hover:underline inline-flex items-center gap-1">
            {site.home_popular_repairs_link} <ArrowRight size={14}/>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {popularRepairs.map((r) => {
            const Icon = ICON_MAP[r.icon] || Sparkles;
            return (
              <Link
                key={r.slug}
                to="/reparatur"
                className="premium-card p-6 group min-h-[230px] flex flex-col"
                data-testid={`repair-card-${r.slug}`}
              >
                <Icon size={24} className="text-[#4d9aff]"/>
                <div className="mt-5 font-display text-xl font-semibold">{r.name}</div>
                <div className="mt-2 text-sm text-zinc-400 leading-relaxed flex-1">{r.desc}</div>
                <div className="mt-6 flex items-center justify-between gap-3">
                  <div><span className="font-display text-lg font-bold">{site.home_from_prefix} {r.from} €</span><div className="mt-1 text-xs text-zinc-500 inline-flex items-center gap-1"><Clock size={12}/>{r.time || site.home_service_time_default}</div></div>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-[#4d9aff]">{site.home_service_cta}<ChevronRight size={15} className="group-hover:translate-x-0.5 transition-transform"/></span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
      )}

      {/* HOW IT WORKS */}
      {SHOW_EXTENDED_HOME_SECTIONS && site.section_how_it_works !== false && (
      <section className="bg-white/[0.03] py-16 lg:py-24" data-testid="how-it-works">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="section-label">{site.home_how_it_works_eyebrow}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
            {site.home_how_it_works_title}
          </h2>

          <div className="mt-12 relative">
            <div className="absolute left-5 top-5 bottom-5 w-px bg-[#0055FF]/35 hidden lg:block" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {steps.map((s, i) => (
              <div key={`step-${i}`} className="card-soft p-6" data-testid={`step-card-${i + 1}`}>
                <div className="h-10 w-10 rounded-full bg-[#0055FF] text-white grid place-items-center font-display font-semibold text-lg">
                  {i + 1}
                </div>
                <div className="mt-5 font-display text-lg font-semibold">{s.title}</div>
                <p className="mt-1 text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
        </div>
      </section>
      )}

      {/* POPULAR BRANDS */}
      {SHOW_EXTENDED_HOME_SECTIONS && site.section_popular_brands !== false && (
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="popular-brands">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="section-label">{site.home_brands_eyebrow}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              {site.home_brands_title}
            </h2>
          </div>
          <Link to="/reparatur" className="text-sm text-[#0055FF] hover:underline hidden sm:inline-flex items-center gap-1">
            {site.home_brands_link} <ArrowRight size={14}/>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {brands.slice(0, 12).map((b) => (
            <Link
              key={b.slug}
              to={`/reparatur/modelle?brand=${b.slug}`}
              className="card-soft card-lift p-5 flex flex-col items-start gap-2"
              data-testid={`brand-card-${b.slug}`}
            >
              <span className="font-display text-lg font-semibold tracking-tight">{b.name}</span>
              {b.popular && <span className="badge-accent">{site.home_brands_popular_badge}</span>}
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* WHY US + QUALITY TABLE */}
      {SHOW_EXTENDED_HOME_SECTIONS && site.section_why_us !== false && (
      <section className="bg-[#131316] text-white py-16 lg:py-24" data-testid="why-us">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="section-label text-zinc-400">{site.home_why_us_eyebrow}</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                {site.home_why_us_title}
              </h2>
              <p className="mt-5 text-zinc-300 leading-relaxed">
                {site.why_us_description}
              </p>
              <ul className="mt-8 space-y-3">
                {whyBullets.map((x, i) => (
                  <li key={`why-${i}`} className="flex items-start gap-2 text-sm text-zinc-200">
                    <BadgeCheck size={16} className="text-[#4DA3FF] mt-0.5 flex-none"/> {x}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              <div className="section-label text-zinc-400 mb-3">{site.home_quality_eyebrow}</div>
              <div className="rounded-2xl border border-white/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-white/5">
                    <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-400">
                      {qualityCols.map((c, i) => (<th key={`qc-${i}`} className="px-4 py-3">{c}</th>))}
                    </tr>
                  </thead>
                  <tbody>
                    {qualityRows.map((q, i) => (
                      <tr key={`qr-${i}`} className={i > 0 ? "border-t border-white/6" : ""}>
                        <td className="px-4 py-3.5 font-medium">{q.name}</td>
                        <td className="px-4 py-3.5 text-zinc-300">{q.brightness}</td>
                        <td className="px-4 py-3.5 text-zinc-300">{q.color}</td>
                        <td className="px-4 py-3.5 text-zinc-300">{q.warranty}</td>
                        <td className="px-4 py-3.5 text-zinc-300 font-mono-accent">{q.price}</td>
                        <td className="px-4 py-3.5 text-zinc-300">{q.available}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-zinc-500">{site.home_quality_note}</p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* REVIEWS */}
      {SHOW_EXTENDED_HOME_SECTIONS && site.section_reviews !== false && (
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="reviews">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="section-label">{site.home_reviews_eyebrow}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">{site.home_reviews_title}</h2>
          </div>
          <a href={site.home_reviews_google_url} target="_blank" rel="noreferrer"
            className="premium-card px-5 py-4 inline-flex items-center gap-4 hover:border-yellow-400/40"
            data-testid="google-reviews-badge">
            <div className="h-10 w-10 rounded-full bg-white grid place-items-center font-display font-bold text-[#4285F4]">G</div>
            <div>
              <div className="flex items-center gap-2"><span className="font-display text-xl font-bold">{site.home_reviews_average}</span><span className="text-yellow-400 tracking-wide">★★★★★</span></div>
              <div className="text-xs text-zinc-400">{site.home_reviews_count} · {site.home_reviews_google_label}</div>
            </div>
            <ExternalLink size={15} className="text-zinc-500"/>
          </a>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.map((r, i) => (
            <div key={r.id || `${r.name}-${i}`} className="premium-card p-6" data-testid={`review-${i}`}>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, j) => (
                  <Star key={`star-${j}`} size={14} className={j < r.rating ? "fill-yellow-400 text-yellow-400" : "text-zinc-700"}/>
                ))}
              </div>
              <p className="mt-4 text-[15px] text-zinc-100 leading-relaxed">"{r.text}"</p>
              <div className="mt-5 flex items-center justify-between gap-3 text-xs text-zinc-500">
                <div><span className="font-medium text-zinc-100">{r.name}</span><span className="mx-2 divider-dot">·</span>{r.repair} · {r.model}</div>
                <span className="inline-flex items-center gap-1 text-emerald-400"><BadgeCheck size={13}/>{site.home_reviews_verified_label}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* GALLERY */}
      {SHOW_EXTENDED_HOME_SECTIONS && site.section_gallery !== false && galleryItems.length > 0 && (
        <section className="premium-section section-rule bg-[#0e0e11]" data-testid="home-gallery">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="section-label text-[#4d9aff]">{site.home_gallery_eyebrow}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">{site.home_gallery_title}</h2>
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 auto-rows-[180px] sm:auto-rows-[240px] gap-3">
              {galleryItems.map((item, i) => (
                <button key={`${item.url}-${i}`} onClick={() => setGalleryImage(item)}
                  className={`group relative overflow-hidden rounded-2xl border border-white/10 ${i === 0 ? "col-span-2 row-span-2" : ""}`}
                  aria-label={`${item.caption}: ${item.alt}`} data-testid={`gallery-image-${i}`}>
                  <img src={item.url} alt={item.alt || ""} loading="lazy" decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute inset-x-0 bottom-0 p-4 text-left text-sm font-medium text-white bg-black/70 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    {item.caption}
                  </span>
                  <span className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/60 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"><ZoomIn size={16}/></span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ preview */}
      {SHOW_EXTENDED_HOME_SECTIONS && site.section_faq !== false && (
      <section className="bg-white/[0.03] py-16 lg:py-24" data-testid="faq-preview">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="section-label">{site.home_faq_eyebrow}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            {site.home_faq_title}
          </h2>
          <div className="mt-10 space-y-3">
            {faqs.map((f, i) => (
              <details key={f.id || `faq-${i}`} className="card-soft p-5 group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer flex items-center justify-between gap-4 list-none">
                  <span className="font-medium text-[15px]">{f.question}</span>
                  <ChevronRight size={16} className="text-zinc-400 group-open:rotate-90 transition-transform"/>
                </summary>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{f.answer}</p>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/faq" className="btn-ghost inline-flex items-center gap-1" data-testid="faq-all-btn">
              {site.home_faq_all_btn} <ChevronRight size={16}/>
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* COMPLETE ACTIVE STORE */}
      {site.section_location_preview !== false && activeLocation !== null && (
        <section className="relative overflow-hidden border-y border-white/10 bg-[#0a0a0c] py-20 lg:py-28" data-testid="location-preview">
          <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-[#0055FF]/10 blur-3xl" />
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {activeLocation ? (
              <div className="relative">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
                  <div className="lg:col-span-8">
                    <div className="section-label text-[#4d9aff]" data-testid="location-eyebrow">{site.home_location_eyebrow || "Standort"}</div>
                    <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="location-heading">Unser Store in <span className="capitalize">{activeLocation.city}</span></h2>
                    {activeLocation.description && <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400 sm:text-base" data-testid="location-description">{activeLocation.description}</p>}
                  </div>
                  <div className="lg:col-span-4 lg:flex lg:justify-end">
                    <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${locationStatus.open ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-300" : "border-zinc-700 bg-zinc-800/70 text-zinc-300"}`} data-testid="location-open-status">
                      <span className={`h-2 w-2 rounded-full ${locationStatus.open ? "bg-emerald-400" : "bg-zinc-500"}`} />{locationStatus.label}
                    </div>
                  </div>
                </div>

                <article className="mt-10 overflow-hidden rounded-[2rem] border border-white/10 bg-[#141417] shadow-2xl shadow-black/30" data-testid={`active-location-${activeLocation.id}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-12">
                    {activeLocation.image_url ? (
                      <div className="relative overflow-hidden lg:col-span-7">
                        <img src={activeLocation.image_url} alt={`${activeLocation.name} Store`} className="h-72 w-full object-cover sm:h-[420px] lg:h-full" loading="lazy" data-testid="location-store-image" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 lg:p-10">
                          <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="location-name">{activeLocation.name}</h3>
                          {(activeLocation.street || activeLocation.city) && <address className="mt-3 flex items-start gap-2 not-italic text-sm leading-relaxed text-zinc-200" data-testid="location-address-value"><MapPin size={16} className="mt-0.5 flex-none text-[#75b2ff]" /><span>{activeLocation.street}{activeLocation.street && (activeLocation.postal_code || activeLocation.city) && <br />}{activeLocation.postal_code} {activeLocation.city}</span></address>}
                        </div>
                      </div>
                    ) : (
                      <div className="relative overflow-hidden p-6 sm:p-8 lg:col-span-5 lg:p-10">
                        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
                        <div className="relative flex h-full min-h-64 flex-col justify-between gap-10">
                          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#0055FF]/15"><Store size={25} className="text-[#4d9aff]" /></div>
                          <div><h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="location-name">{activeLocation.name}</h3>{(activeLocation.street || activeLocation.city) && <address className="mt-3 flex items-start gap-2 not-italic text-sm leading-relaxed text-zinc-300" data-testid="location-address-value"><MapPin size={16} className="mt-0.5 flex-none text-[#75b2ff]" /><span>{activeLocation.street}{activeLocation.street && (activeLocation.postal_code || activeLocation.city) && <br />}{activeLocation.postal_code} {activeLocation.city}</span></address>}</div>
                        </div>
                      </div>
                    )}

                    <div className={`p-6 sm:p-8 lg:p-10 ${activeLocation.image_url ? "lg:col-span-5" : "lg:col-span-7"}`}>
                      <div className="flex items-center gap-3"><div className="grid h-11 w-11 place-items-center rounded-xl bg-[#0055FF]/15"><Clock size={20} className="text-[#4d9aff]" /></div><div><div className="section-label text-zinc-500">Besuchszeiten</div><h3 className="mt-1 font-display text-xl font-semibold">Öffnungszeiten</h3></div></div>
                      <dl className="mt-6 divide-y divide-white/[0.07]" data-testid="location-opening-hours">
                        {DAY_KEYS.map((day) => { const hours = activeLocation.opening_hours?.[day]; return <div key={day} className="flex items-center justify-between gap-5 py-2.5 text-sm" data-testid={`location-hours-${day}`}><dt className="font-medium text-zinc-300">{DAY_LABELS_SHORT[day]}</dt><dd className={hours?.closed ? "text-zinc-600" : "tabular-nums text-zinc-100"}>{formatHours(hours)}</dd></div>; })}
                      </dl>

                      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2" data-testid="location-contact-details">
                        {activeLocation.phone && <a href={toTelHref(activeLocation.phone)} className="rounded-2xl border border-white/10 bg-black/20 p-4 transition-colors hover:border-[#4d9aff]/40" data-testid="location-phone-link"><div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"><Phone size={13} className="text-[#4d9aff]" /> Telefon</div><div className="mt-2 text-sm font-semibold text-white" data-testid="home-phone">{activeLocation.phone}</div></a>}
                        {activeLocation.whatsapp && <a href={toWhatsAppHref(activeLocation.whatsapp)} target="_blank" rel="noreferrer" className="rounded-2xl border border-white/10 bg-black/20 p-4 transition-colors hover:border-emerald-400/40" data-testid="location-whatsapp-detail"><div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"><MessageCircle size={13} className="text-emerald-400" /> WhatsApp</div><div className="mt-2 text-sm font-semibold text-white">{activeLocation.whatsapp}</div></a>}
                        {activeLocation.email && <a href={`mailto:${activeLocation.email}`} className="rounded-2xl border border-white/10 bg-black/20 p-4 transition-colors hover:border-[#4d9aff]/40 sm:col-span-2 lg:col-span-1 xl:col-span-2" data-testid="location-email-link"><div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"><Mail size={13} className="text-[#4d9aff]" /> E-Mail</div><div className="mt-2 break-all text-sm font-semibold text-white">{activeLocation.email}</div></a>}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-white/10 p-6 sm:p-8 lg:p-10">
                    <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
                      <div className="flex flex-wrap gap-3">
                        {activeLocation.google_maps_url && locationButtons.maps && <a href={activeLocation.google_maps_url} target="_blank" rel="noreferrer" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#0055FF] px-5 py-3 text-sm font-bold text-white transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#1768ff]" data-testid="location-maps-button"><MapPin size={16} />{locationButtons.maps}<ExternalLink size={13} /></a>}
                        {activeLocation.google_maps_url && locationButtons.route && <a href={activeLocation.google_maps_url} target="_blank" rel="noreferrer" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-3 text-sm font-bold text-white transition-[transform,border-color] hover:-translate-y-0.5 hover:border-white/30" data-testid="location-route-button"><Navigation size={16} />{locationButtons.route}</a>}
                        {activeLocation.phone && locationButtons.call && <a href={toTelHref(activeLocation.phone)} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-3 text-sm font-bold text-white transition-[transform,border-color] hover:-translate-y-0.5 hover:border-white/30" data-testid="location-call-button"><Phone size={16} />{locationButtons.call}</a>}
                        {activeLocation.whatsapp && locationButtons.whatsapp && <a href={toWhatsAppHref(activeLocation.whatsapp)} target="_blank" rel="noreferrer" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-bold text-zinc-950 transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-emerald-400" data-testid="location-whatsapp-button"><MessageCircle size={16} />{locationButtons.whatsapp}</a>}
                      </div>
                      {Object.entries(activeLocation.social_links || {}).some(([key, url]) => LOCATION_SOCIALS[key] && url) && <div className="flex flex-wrap items-center gap-2" data-testid="location-social-links">{Object.entries(activeLocation.social_links || {}).map(([key, url]) => { const social = LOCATION_SOCIALS[key]; if (!social || !url) return null; const Icon = social.icon; return <a key={key} href={url} target="_blank" rel="noreferrer" aria-label={social.label} className="grid h-11 w-11 place-items-center rounded-full border border-white/10 text-zinc-400 transition-[transform,color,border-color] hover:-translate-y-0.5 hover:border-white/30 hover:text-white" data-testid={`location-social-${key}`}><Icon size={18} /></a>; })}</div>}
                    </div>
                  </div>
                </article>

                {locationGallery.length > 0 && <div className="mt-12" data-testid="location-gallery"><div className="flex items-end justify-between gap-4"><div><div className="section-label text-zinc-500">Store-Einblicke</div><h3 className="mt-2 font-display text-2xl font-semibold">Unser Store in Bildern.</h3></div>{locationGallery.length > 6 && locationButtons.gallery && <button onClick={() => { setLocationGalleryIndex(0); setLocationGalleryOpen(true); }} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:border-white/30" data-testid="location-view-all-photos"><Images size={16} />{locationButtons.gallery}</button>}</div><div className="mt-6 grid auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[210px] lg:grid-cols-4">{locationGallery.slice(0, 6).map((image, index) => <button key={`${image.url}-${index}`} onClick={() => { setLocationGalleryIndex(index); setLocationGalleryOpen(true); }} className={`group relative overflow-hidden rounded-2xl border border-white/10 ${index === 0 ? "col-span-2 row-span-2" : ""}`} aria-label={image.alt || `Storebild ${index + 1}`} data-testid={`location-gallery-image-${index}`}><img src={image.url} alt={image.alt || ""} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /><span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/15" /></button>)}</div></div>}
              </div>
            ) : <div className="h-80 animate-pulse rounded-[2rem] border border-white/10 bg-white/[0.03]" aria-label="Standort wird geladen" data-testid="location-loading" />}
          </div>
        </section>
      )}

      <section className="section-rule py-16 lg:py-20" data-testid="home-final-cta">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-[#0055FF] px-6 py-10 sm:px-10 lg:px-14 lg:py-14 text-white">
            <div className="grain-overlay" />
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-2xl">
                <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">{site.home_section_cta_title}</h2>
                <p className="mt-3 text-blue-100">{site.home_section_cta_text}</p>
              </div>
              <Link to="/reparatur" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-zinc-950 px-6 py-3 font-semibold hover:bg-blue-50"
                data-testid="home-final-cta-button">{site.home_section_cta_button}<ArrowRight size={17}/></Link>
            </div>
          </div>
        </div>
      </section>

      {locationGalleryOpen && locationGallery.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/95 p-4 backdrop-blur-md" role="dialog" aria-modal="true" aria-label="Store-Galerie" data-testid="location-gallery-modal">
          <button onClick={() => setLocationGalleryOpen(false)} className="absolute right-5 top-5 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Galerie schließen" data-testid="location-gallery-close"><X size={20} /></button>
          <div className="mx-auto flex h-full max-w-6xl flex-col justify-center">
            <img src={locationGallery[locationGalleryIndex]?.url} alt={locationGallery[locationGalleryIndex]?.alt || ""} className="max-h-[68vh] w-full rounded-2xl object-contain" data-testid="location-gallery-active-image" />
            <div className="mt-4 flex items-center justify-between gap-4 text-sm text-zinc-300"><span>{locationGallery[locationGalleryIndex]?.alt || `${activeLocation?.name} · Bild ${locationGalleryIndex + 1}`}</span><span className="tabular-nums">{locationGalleryIndex + 1} / {locationGallery.length}</span></div>
            <div className="mt-5 flex gap-3 overflow-x-auto pb-2" data-testid="location-gallery-thumbnails">{locationGallery.map((image, index) => <button key={`${image.url}-modal-${index}`} onClick={() => setLocationGalleryIndex(index)} className={`h-20 w-24 flex-none overflow-hidden rounded-xl border-2 ${locationGalleryIndex === index ? "border-[#4d9aff]" : "border-transparent opacity-60 hover:opacity-100"}`} aria-label={`Bild ${index + 1} anzeigen`} data-testid={`location-gallery-thumb-${index}`}><img src={image.url} alt="" className="h-full w-full object-cover" /></button>)}</div>
          </div>
        </div>
      )}


      {galleryImage && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm grid place-items-center p-4" role="dialog" aria-modal="true" aria-label={galleryImage.caption}
          data-testid="gallery-lightbox" onClick={() => setGalleryImage(null)}>
          <button onClick={() => setGalleryImage(null)} className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 grid place-items-center hover:bg-white/20"
            aria-label="Galerie schließen" data-testid="gallery-lightbox-close"><X size={20}/></button>
          <figure className="max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <img src={galleryImage.url} alt={galleryImage.alt || ""} className="max-h-[78vh] w-auto rounded-2xl object-contain" />
            {galleryImage.caption && <figcaption className="mt-4 text-center text-sm text-zinc-300">{galleryImage.caption}</figcaption>}
          </figure>
        </div>
      )}
    </div>
  );
}
